import React from 'react';
import {
  Box,
  Heading,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useBreakpointValue,
  useColorModeValue
} from '@chakra-ui/react';
import { MapTrifold, ListBullets } from '@phosphor-icons/react';
import SiteLocationMap from './SiteLocationMap';
import SublocationsManager from '../admin/SublocationsManager';

export default function SiteLocationSection({
  site,
  media,
  onMediaClick,
  hiddenLocations,
  onVisibilityChange
}) {
  // Responsive layout
  const mapHeight = useBreakpointValue({ base: "300px", md: "400px" });

  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const tabBg = useColorModeValue('gray.50', 'gray.700');
  const activeTabBg = useColorModeValue('white', 'gray.800');

  if (!site.location?.coordinates) {
    return null;
  }

  return (
    <Box>
      <Heading size="md" mb={4}>Site Location</Heading>
      
      {/* Desktop: Side-by-side layout */}
      <Grid 
        templateColumns={{ base: "1fr", lg: "3fr 1fr" }}
        gap={6}
        display={{ base: "none", md: "grid" }}
      >
        {/* Map */}
        <GridItem>
          <Box 
            borderRadius="lg" 
            overflow="hidden" 
            height={mapHeight}
            boxShadow="md"
          >
            <SiteLocationMap 
              siteId={site.id}
              mainLocation={{
                latitude: site.location.coordinates.lat,
                longitude: site.location.coordinates.lng
              }}
              media={media}
              filteredMedia={media}
              onMediaClick={onMediaClick}
              hiddenLocations={hiddenLocations}
            />
          </Box>
        </GridItem>
        
        {/* Sublocations Manager */}
        <GridItem>
          <Box 
            height="100%" 
            maxHeight={mapHeight} 
            overflowY="auto"
            borderRadius="lg"
            boxShadow="md"
            p={4}
            bg={cardBg}
          >
            <Heading size="sm" mb={4}>Site Locations</Heading>
            <SublocationsManager 
              siteId={site.id}
              site={site}
              onVisibilityChange={onVisibilityChange}
            />
          </Box>
        </GridItem>
      </Grid>
      
      {/* Mobile: Tabbed layout */}
      <Box display={{ base: "block", md: "none" }}>
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab 
              _selected={{ bg: activeTabBg, borderBottomColor: activeTabBg }} 
              bg={tabBg}
              fontWeight="medium"
              leftIcon={<MapTrifold size={16} />}
            >
              Map
            </Tab>
            <Tab 
              _selected={{ bg: activeTabBg, borderBottomColor: activeTabBg }} 
              bg={tabBg}
              fontWeight="medium"
              leftIcon={<ListBullets size={16} />}
            >
              Locations
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0} pt={4}>
              <Box 
                borderRadius="lg" 
                overflow="hidden" 
                height="300px"
                boxShadow="md"
              >
                <SiteLocationMap 
                  siteId={site.id}
                  mainLocation={{
                    latitude: site.location.coordinates.lat,
                    longitude: site.location.coordinates.lng
                  }}
                  media={media}
                  filteredMedia={media}
                  onMediaClick={onMediaClick}
                  hiddenLocations={hiddenLocations}
                />
              </Box>
            </TabPanel>
            <TabPanel p={0} pt={4}>
              <Box>
                <SublocationsManager 
                  siteId={site.id}
                  site={site}
                  onVisibilityChange={onVisibilityChange}
                />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
